// ---------------------------------------------------------------------------
// The player identity chip.
//
// One rule here matters more than every visual detail: a signed-in player's
// username lives in its own boxed mono slot and is never concatenated into the
// display name. Display names may contain parentheses, so `Hyper` + `@isaac`
// must be structurally different from a display name that literally reads
// `Hyper (isaac)`. Anyone who can fake a handle by choosing a display name can
// impersonate a signed-in player, so the slot is a structural element, never
// hover-only and never assembled out of a string.
//
// The same slot carries the `guest` tag and the bot's robot-plus-difficulty
// chip, so the three kinds of player are told apart in one glance at one place.
// `showIdentity={false}` takes the human half of it away for a caller that has
// no room — see the note on the prop. It removes the slot; it never replaces it
// with something that reads like one.
//
// The dot's colour is a prop rather than `player.color` read straight off the
// record, because teams own colour outright: while teams are on, a player wears
// their side's colour and not their own. Callers pass
// `displayColorOf(state, player)`; `player.color` is only the fallback for the
// few places that draw a player with no state to hand.
//
// **The chip's height is a fixed number per size and nothing may change it.**
// This component is also the reveal row, the standing on the reveal's closing
// slide, the end screen and the game record, so a chip that grew when it had
// more to say would move every row under it and would stagger a roster of
// humans against a roster of AIs. `SIZES` carries the number: 25 / 26 / 28px,
// which is what the chip already measured — the tallest thing in it is the
// boxed handle slot, `11/12/13px` of text on `py-1`. The floor is on the row
// itself, so a bot chip (which has no handle slot and used to come out 2px
// shorter) matches the player beside it instead of nearly matching it, and a
// chip with its identity slot suppressed keeps the same number rather than
// collapsing to the height of its own name. **A disconnected chip is that same
// number**, rather than the 10px more it used to stand at — the dashed box it
// carried is gone. See the note on the row's className.
//
// **An AI opponent's model id goes under BOTH the name and the badge, inside
// that number.** Three arrangements have been tried and the difference between
// them is worth writing down, because each looks like a tidy-up of the last:
//
//   1. Inline after the badge, `hidden sm:block`. The id was never once on
//      screen on a phone, which is where the game is played.
//   2. Stacked under the badge alone, with the name left centred beside it. The
//      arithmetic worked and it read as a caption on the badge rather than as a
//      line about the player.
//   3. This one. The name, the dot and the badge are one row that gives up its
//      vertical padding, and the id is a second row under all of it.
//
// The pixels: at `md` the two rows are 14 and 12 into 26, and the name drops
// from 16px to 14px with its line box pinned to 14. That shrink is the price of
// the constant height and it is deliberate — a 16px name wants a 19px glyph box
// and there is no 19 to be had beside a 12px line inside a 26px chip. It is
// paid only by the chips that carry an id, which are AI opponents, which are
// the chips that have something extra to say.
//
// **The id is out of flow inside a box of a known height.** A model id is one
// unbreakable 30-to-40 character word, so in normal flow its max-content would
// be the whole stack's max-content, and in a width-constrained row — which is
// every row in the lobby, the reveal and the end screen — the stack would
// shrink to fit and the *name* above it would truncate to pay for it. `Rachel`
// losing 0.12px of its box is not a narrower name, it is `Rac…`, because
// text-overflow gives up two whole characters for a fraction of one.
// Absolutely positioned, the id asks for no width at all, stretches to whatever
// the stack got, and truncates on its own. This replaces a `shrink-[9999]` +
// `min-w-0` + `overflow-hidden` arrangement that bought the same guarantee by
// making the shortfall land on the id to the last pixel. The guarantee is what
// has to survive an edit here, not the mechanism.
// ---------------------------------------------------------------------------

import type { ReactNode, ReactElement } from 'react';
import type { PublicPlayer } from '@countdown/shared';
import { Crown, Padlock, RobotGlyph } from './glyphs.js';
import { botLabel } from '../../lib/playerLookup.js';

export interface PlayerChipProps {
  player: PublicPlayer;
  /**
   * What to draw the dot in. Pass `displayColorOf(state, player)` wherever
   * client state is available; it falls back to the player's own colour.
   *
   * **Null means there is no colour and the dot is painted with none**, which
   * is a real state rather than a missing prop: past `COLORED_SEATS` seats
   * nobody in the room has one — `colorForSeat` decides that once, for the whole
   * room — and a grey stand-in would be a colour that reads as an identity.
   * Undefined still means "I have no state to hand, use the player's own".
   */
  color?: string | null;
  /** Independent markers, each optional. */
  showHostCrown?: boolean;
  showSeatLock?: boolean;
  showMuted?: boolean;
  /**
   * The human identity slot: `@handle`, `guest`, and `signed in`.
   *
   * On by default, and only a caller with a genuine space problem should turn
   * it off — the reveal's leaderboard on a phone is the case it exists for,
   * where eight rows of name plus boxed handle plus score is unreadable.
   *
   * **All three states go together.** Hiding the handle and keeping `guest`
   * would leave a leaderboard where guests are tagged and account holders are
   * not, which reads as a mark against the guests rather than as the fact it
   * is. The bot badge is not part of this and never hides: that a player is a
   * machine is a different kind of fact from what they are called, and the
   * model id hangs off it.
   *
   * It never *replaces* the slot with anything. A chip that showed a handle
   * some other way — appended to the name, on hover — would break the rule at
   * the top of this file, which is the only rule in here that is a security
   * property rather than a layout one.
   */
  showIdentity?: boolean;
  teamName?: string;
  teamColor?: string;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Rendered against the name itself, after the identity slot.
   *
   * For a control that acts on *this* player rather than a fact about them.
   * `trailing` is pushed to the far end of the row by `ml-auto`, which is the
   * wrong place for a control the player has to read as belonging to their own
   * name.
   *
   * **It has no caller as of this pass.** The lobby's rename pencil was the
   * only one and it moved out to the row around the chip, because a 44px button
   * inside a 26px chip made that one seat 18px taller than every other seat in
   * the list and took the width the name needed. Anything reaching for this
   * again should check those two things first; if nothing does, delete it.
   */
  afterName?: ReactNode;
  /**
   * Rendered to the right of the name, e.g. a score or a lock chip.
   *
   * Centred against the whole chip rather than against the name's own line.
   * It is usually a score or a ready marker read down a column of rows, and
   * the chip is a fixed height, so centring puts every one of them on the same
   * line whether or not the row it sits in is an AI carrying a model id.
   * Aligning to the name instead would shift it by a pixel or two per row and
   * the column would waver.
   */
  trailing?: ReactNode;
}

// `height` is the chip, in both connection states. There was a `heightOffline`
// beside it — the same number plus the dashed offline box's `py-1` and its
// border — and it went with the box; see the row's className.
//
// The next four are the AI stack, and they are solved against `height` rather
// than chosen: `nameRow` and `modelRow` add up to it exactly — 14 and 12 into
// 26 at `md` — because every pixel the name row does not need is a pixel the id
// has to have. `nameModel` is the name inside that row, pinned to the row's own
// height, which is the shrink the constant height is paid for with.
//
// All of them were measured in the shipped fonts. IBM Plex Mono at 10px is 13px
// of glyph box and its ink is 12.1 of that, so a 12px line clips nothing a
// model id contains. The badge's label is uppercase, which has no descenders at
// all, so it sits in a box one pixel under its own glyph box happily.
// `RobotGlyph` stands 15/14 of the size it is given, which is why the glyph
// steps down with the row rather than staying at the 13 the one-line badge
// uses.
const SIZES = {
  sm: {
    dot: 7,
    name: 'text-[14px]',
    nameModel: 'text-[13px] leading-[13px]',
    handle: 'text-[11px]',
    gap: 'gap-2',
    indent: 15,
    height: 'min-h-[25px]',
    nameRow: 'h-[13px]',
    botGlyph: 11,
    modelRow: 'h-[12px]',
    modelText: 'text-[10px] leading-[12px]',
  },
  md: {
    dot: 9,
    name: 'text-[16px]',
    nameModel: 'text-[14px] leading-[14px]',
    handle: 'text-[12px]',
    gap: 'gap-2.5',
    indent: 19,
    height: 'min-h-[26px]',
    nameRow: 'h-[14px]',
    botGlyph: 12,
    modelRow: 'h-[12px]',
    modelText: 'text-[10px] leading-[12px]',
  },
  lg: {
    dot: 10,
    name: 'text-[18px]',
    nameModel: 'text-[16px] leading-[16px]',
    handle: 'text-[13px]',
    gap: 'gap-3',
    indent: 22,
    height: 'min-h-[28px]',
    nameRow: 'h-[16px]',
    botGlyph: 13,
    modelRow: 'h-[12px]',
    modelText: 'text-[10px] leading-[12px]',
  },
} as const;

/**
 * The robot-plus-difficulty slot, in one of its two boxes: `py-1` on its own,
 * or a fixed row height when a model id is stacked under it. One component
 * rather than two spans, because the difference between them is the box and
 * must stay the box — the glyph, the label and `data-player-slot` are the same
 * slot either way, and a bot without a model must go on looking exactly as it
 * did.
 */
function BotSlot({
  player,
  box,
  glyph,
}: {
  player: PublicPlayer;
  box: string;
  glyph: number;
}): ReactElement {
  return (
    <span
      // Opaque, like the guest slot. Translucent, it compounded with the wash on
      // a leader row and the label fell below 4.5:1.
      className={`flex shrink-0 items-center gap-1.5 rounded-[5px] bg-raised text-blue-200 ${box}`}
      data-player-slot="bot"
    >
      <RobotGlyph size={glyph} />
      <span className="font-mono text-[10px] font-semibold tracking-[0.08em] uppercase">
        {botLabel(player)}
      </span>
    </span>
  );
}

export function PlayerChip({
  player,
  color,
  showHostCrown = false,
  showSeatLock = false,
  showMuted = false,
  showIdentity = true,
  teamName,
  teamColor,
  size = 'md',
  afterName,
  trailing,
}: PlayerChipProps): ReactElement {
  const metrics = SIZES[size];
  // Signed in and no handle yet is its own state, and reading a missing username
  // as "guest" put a guest tag on real accounts: the user row is only written
  // when somebody claims a username, so anybody who signed in and left
  // onboarding half done landed here.
  const isGuest = !player.registered && !player.isBot;
  // `??` on the prop being absent, and then a separate null check, because the
  // two nulls mean different things: `color === undefined` is a caller with no
  // state to hand, and a resolved null is a room with no colours in it at all.
  const dotColor = color === undefined ? player.color : color;
  // Only a bot has one and only a bot's is drawn, so the height, the name's
  // size and the second row are all decided by one answer rather than by three
  // that could disagree.
  const model = player.isBot ? player.botModel : null;

  const dot = (
    // `backgroundColor` rather than the `background` shorthand: a shorthand does
    // not transition, and this dot changes colour the moment a player joins or
    // leaves a team.
    //
    // **A null colour paints nothing and the slot stays.** Past `COLORED_SEATS`
    // seats nobody in the room has a colour — `colorForSeat` decides that once,
    // for the whole room — and the answer is no paint rather than a grey, which
    // would itself be a colour and would read as an identity. The element
    // survives because it is more than a swatch: on the lobby it is the tap
    // target that opens the edit sheet, and a room with no colours still has
    // people in it to edit. `undefined` and not `'transparent'`, so nothing is
    // declared at all.
    <span
      aria-hidden="true"
      className="shrink-0 rounded-full transition-colors duration-200 ease-out"
      data-player-dot
      style={{ width: metrics.dot, height: metrics.dot, backgroundColor: dotColor ?? undefined }}
    />
  );

  const name = (
    <span
      className={`truncate font-semibold ${model === null ? metrics.name : metrics.nameModel}`}
      data-player-name
    >
      {player.displayName}
    </span>
  );

  return (
    <div
      // **A player who is not there recedes, and that is the whole of it.**
      // `opacity-70` is the entire offline treatment; every other property —
      // height, padding, radius, the horizontal rhythm — is identical in both
      // states, so a drop moves nothing and shifts no neighbour.
      //
      // This reverses a dashed `rounded-chip border border-dashed
      // border-line-strong px-2 py-1` box around the offline chip, and both
      // halves of that were wrong.
      //
      // The colour first: `line-strong` is a mid-tone in both themes (the
      // palette note in CLAUDE.md is about text on it, and the same fact is why
      // it is visible at all here), and against these dark surfaces it reads
      // bluish. Isaac read it as "a blue border around the player that
      // disconnected" and asked what it was for, which is the answer — it did
      // not say offline, it said *look here*.
      //
      // And that is the deeper half: a border is emphasis, and emphasis is
      // backwards for somebody who is not in the room. The person who has
      // dropped is the least important row in the roster for as long as they
      // are gone. No border replaces it in any colour, because there is nothing
      // a box round this row can say that the fade and the `disconnected` tag
      // below do not already say, and anything drawn round it competes with the
      // tag for the same job.
      //
      // The `min-h` pair went with the box. There were two numbers — `height`
      // and a `heightOffline` 10px taller — written as ONE declaration chosen
      // by the connected state, because a second declaration of the same
      // property would have been settled by the order Tailwind happened to emit
      // them in rather than by the order they were written here. That was load
      // bearing while the box existed and is moot now: one height for both
      // states is a stronger version of the guarantee it was bought to make,
      // since the row cannot change height when there is only one height to
      // have. If a future offline treatment ever adds a box back, it needs that
      // single-declaration trick back with it.
      className={`flex min-w-0 items-center transition-opacity duration-200 ease-out ${metrics.gap} ${metrics.height} ${player.connected ? '' : 'opacity-70'}`}
      data-player-id={player.id}
    >
      {teamColor && (
        <span
          aria-hidden="true"
          className="shrink-0 rounded-full transition-colors duration-200 ease-out"
          style={{ width: 5, height: 16, backgroundColor: teamColor }}
        />
      )}

      {model === null ? (
        <>
          {dot}
          {name}
          {player.isBot ? <BotSlot player={player} box="px-2 py-1" glyph={13} /> : null}
        </>
      ) : (
        // The AI stack: the dot, the name and the badge on one row that has
        // given up its padding, and the model id on a second row under all
        // three. `min-w-0` because the row's own min-content is the badge, and
        // without it the stack could not shrink and would push the score off
        // the end of the chip.
        <span className="flex min-w-0 flex-col">
          <span className={`flex min-w-0 items-center ${metrics.gap} ${metrics.nameRow}`}>
            {dot}
            {name}
            <BotSlot player={player} box={`${metrics.nameRow} px-2`} glyph={metrics.botGlyph} />
          </span>
          <span className={`relative block w-full ${metrics.modelRow}`}>
            <span
              // The vendor prefix is dropped — everyone can read
              // `gemini-3.6-flash` and nobody needed the `google/`. The whole
              // id stays in the tooltip and in `data-player-model`.
              className={`absolute inset-y-0 right-0 truncate font-mono text-ink-dim ${metrics.modelText}`}
              style={{ left: metrics.indent }}
              data-player-model={model}
              title={model}
            >
              {model.split('/').pop()}
            </span>
          </span>
        </span>
      )}

      {player.isBot || !showIdentity ? null : isGuest ? (
        <span
          className="shrink-0 rounded-[5px] bg-line px-1.5 py-1 font-mono text-[10px] font-semibold tracking-[0.08em] text-ink-muted uppercase"
          data-player-slot="guest"
        >
          guest
        </span>
      ) : player.username === null ? (
        // An account partway through onboarding. The slot still says what this
        // player is, because the alternative reads as `@null` and the one before
        // it read as a guest.
        <span
          className="shrink-0 rounded-[5px] bg-line px-1.5 py-1 font-mono text-[10px] font-semibold tracking-[0.08em] text-ink-muted uppercase"
          data-player-slot="signed-in"
        >
          signed in
        </span>
      ) : (
        <span
          className={`shrink-0 rounded-[5px] bg-raised px-1.5 py-1 font-mono text-ink-dim ${metrics.handle}`}
          data-player-slot="handle"
        >
          @{player.username}
        </span>
      )}

      {afterName}

      {showHostCrown && <Crown size={size === 'sm' ? 16 : 20} className="ml-0.5" />}

      {showSeatLock && (
        <span className="flex shrink-0 items-center gap-1.5 text-ink-dim">
          <Padlock size={13} />
          <span className="font-mono text-[10px] font-semibold tracking-[0.08em] uppercase">seat locked</span>
        </span>
      )}

      {/* A tag rather than a glyph: `muted` cannot be mistaken for a volume
          control the reader is invited to press. */}
      {showMuted && (
        <span className="shrink-0 rounded-[5px] bg-surface-2 px-1.5 py-1 font-mono text-[10px] font-semibold tracking-[0.08em] text-ink-dim uppercase">
          muted
        </span>
      )}

      {/* `ml-auto`, so it lands in the same right-hand column a lobby row's
          `ready` / `waiting` marker sits in. That is not tidiness: the lobby
          now draws no ready marker at all for a player who has dropped (see
          `readyMarker` in LobbyScreen), and a tag that appears where the marker
          it displaced used to be reads as having replaced it, where one left
          against the name reads as the marker having simply vanished.

          On a chip that also has `trailing` — a score, on the reveal and the
          end screen — this takes the free space and the score sits immediately
          after it, so the two stay a right-aligned pair rather than one drifting
          left. */}
      {!player.connected && (
        <span className="ml-auto shrink-0 font-mono text-[10px] font-semibold tracking-[0.08em] text-alert uppercase">
          disconnected
        </span>
      )}

      {teamName && <span className="shrink-0 font-mono text-[11px] text-ink-dim">{teamName}</span>}

      {trailing && <span className="ml-auto shrink-0">{trailing}</span>}
    </div>
  );
}
